/**
 * nextSteps.ts — the Next steps card's pure rules (deal detail slice 4,
 * §39.9 order 4; spec §3.4 B). Tasks are activities of type 'task' (D3-1a: no
 * deal_tasks table); nothing here fetches.
 *
 *   - SUGGESTIONS: the §3.4 B stage table, in its order, at most two. Due
 *     days are working days after the server's today on London calendar days
 *     (R1). The table gives Qualified and Negotiation no offsets; they take
 *     the next working day — OUR choice, recorded in the slice's record.
 *   - A chip disappears once a task with its title exists on the deal, open
 *     or done (trimmed, any case).
 *   - DONE means status 'done'. The update route that reopens a task sets
 *     the status and leaves completed_at behind, so completed_at alone is not
 *     evidence (the old Tasks card read it, and a reopened task stayed ticked).
 *   - The row's "Due {date} · {assignee}" carries only what the wire has. The
 *     create route has no way to set an assignee (CreateActivityRequest has no
 *     employee id, and the legacy owner text is not written), so a task made
 *     here carries none and the row says only when it is due.
 */
import type { SalesActivity } from '../../../../api/types'
import type { DealStage } from '../../types'
import { addWorkingDaysIso, dueDayOf, isWorkingDay } from './workingDays'
import { formatDay } from './dealRail'
import { fmtDay } from './logActivitySheet'

export interface Suggestion { title: string; due: string }

type Rule = { title: (contact: string | null) => string | null; days: number }
const plain = (t: string) => () => t

const RULES: Partial<Record<DealStage, readonly Rule[]>> = {
  'Qualified': [{ title: plain('Send proposal'), days: 1 }, { title: plain('Confirm budget and timeline'), days: 1 }],
  'Proposal Sent': [{ title: plain('Follow up on proposal'), days: 3 }],
  'Negotiation': [{ title: plain('Agree terms'), days: 1 }, { title: plain('Send contract'), days: 1 }],
}
const NEW_LEAD_NO_CONTACT: readonly Rule[] = [{ title: plain('Find the decision maker'), days: 1 }, { title: plain('Book a discovery call'), days: 4 }]
const NEW_LEAD_WITH_CONTACT: readonly Rule[] = [{ title: c => (c ? `Send intro email to ${c}` : null), days: 1 }, { title: plain('Book a discovery call'), days: 4 }]

const norm = (s: string) => s.trim().toLowerCase()

/** Up to two chips for the deal's stage. `contact` is null when the deal has
 *  no linked contact; a linked contact whose name is not known yet gives no
 *  intro chip rather than a sentence with a hole in it. */
export function suggestionsFor(stage: DealStage, contact: { name: string } | null, todayIso: string, activities: readonly SalesActivity[]): Suggestion[] {
  const rules = stage === 'New Lead' ? (contact ? NEW_LEAD_WITH_CONTACT : NEW_LEAD_NO_CONTACT) : (RULES[stage] ?? [])
  const name = contact ? contact.name.trim() : null
  const existing = new Set(activities.filter(a => a.activityType === 'task').map(a => norm(a.subject)))
  const out: Suggestion[] = []
  for (const r of rules) {
    const title = r.title(name)
    if (!title || existing.has(norm(title))) continue
    out.push({ title, due: addWorkingDaysIso(todayIso, r.days) })
  }
  return out.slice(0, 2)
}

export const isTaskDone = (t: Pick<SalesActivity, 'status'>): boolean => t.status === 'done'

/** The header's right-hand side: "None yet", "1 open task", "{n} open tasks". */
export function openTaskLabel(tasks: readonly SalesActivity[]): string {
  const n = tasks.filter(t => !isTaskDone(t)).length
  if (n === 0) return 'None yet'
  return n === 1 ? '1 open task' : `${n} open tasks`
}

/** "Due 16 Oct 2026 · Colin Miscoded" — only the parts the wire carries. */
export function taskMeta(t: Pick<SalesActivity, 'dueAt' | 'assignedTo'>): string | null {
  const day = dueDayOf(t.dueAt)
  const parts = [day ? `Due ${formatDay(day, 'utc')}` : '', (t.assignedTo ?? '').trim()].filter(Boolean)
  return parts.length ? parts.join(' · ') : null
}

/** Open first by due day (undated last), then done; ties keep the wire order. */
export function sortTasks(tasks: readonly SalesActivity[]): SalesActivity[] {
  const key = (t: SalesActivity) => dueDayOf(t.dueAt) ?? '9999-99-99'
  return tasks.map((t, i) => ({ t, i })).sort((a, b) => {
    const da = isTaskDone(a.t) ? 1 : 0, db = isTaskDone(b.t) ? 1 : 0
    if (da !== db) return da - db
    const ka = key(a.t), kb = key(b.t)
    if (ka !== kb) return ka < kb ? -1 : 1
    return a.i - b.i
  }).map(x => x.t)
}

/** A chip's day: "Tue 13 Oct" (spec §3.7). */
export const chipDay = (iso: string): string => fmtDay(iso)

/** The manual add's Due picker: the Log activity sheet's task choices, plus
 *  "No due date" — the old Tasks card added tasks with none, and that stays
 *  possible. */
export type DueChoice = 'next' | 'today' | 'week' | 'pick' | 'none'
export const DUE_CHOICES: readonly { value: DueChoice; label: string }[] = [
  { value: 'next', label: 'Next working day' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'In a week' },
  { value: 'pick', label: 'Pick date' },
  { value: 'none', label: 'No due date' },
]

/** The due DAY a choice names ('' for none, or for Pick date with no day). A
 *  week out that lands on a weekend or bank holiday moves to the next
 *  working day, as the sheet's does. */
export function resolveDueChoice(choice: DueChoice, todayIso: string, picked: string): string {
  switch (choice) {
    case 'today': return todayIso
    case 'next': return addWorkingDaysIso(todayIso, 1)
    case 'week': {
      const week = new Date(Date.parse(`${todayIso}T00:00:00Z`) + 7 * 86_400_000).toISOString().slice(0, 10)
      return isWorkingDay(week) ? week : addWorkingDaysIso(week, 1)
    }
    case 'pick': return /^\d{4}-\d{2}-\d{2}$/.test(picked) ? picked : ''
    default: return ''
  }
}
